import { GPC, ValuationApproachGPC } from 'api';
import { GRID_NUMBER_CHECKBOX } from 'common/constants/gridType';
import { DISABLED_PERFORMANCE_ROW_CLASS } from 'common/constants/valuations';
import {
  largeCurrencyFormat,
  oneDecimalPercentFormat,
  oneDecimalPercentFormatValidateFloatTrue,
} from 'common/formats/formats';
import { Cells, GridType } from 'common/types/scalarSpreadsheet';
import NumberCheckbox from 'components/ScalarSpreadsheet/components/number-multiple-checkbox/NumberMultipleCheckbox';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import {
  COMPARISON_VOLATILITY_YEARS_CELL,
  MEDIAN_LABEL,
  NET_INCOME_DESCRIPTION,
  PERCENTILE_25,
  PERFORMANCE_METRICS_SPREADSHEET_RANK_VOLATILITY_HEADER_CLASSNAME,
  SHEET_ALIASES_CONSTANTS,
  SHEET_TITLES_CONSTANTS,
  TANGIBLE_BOOK_VALUE_DESCRIPTION,
  VOLATILITY_YEARS,
  VOLATILITY_YEARS_COMPANY_CELL,
  VOLATILITY_YEARS_MAPPING,
} from 'pages/Valuations/approaches/guidelinePublicCompanies/PerformanceMetrics/common/constants/performanceMetrics';
import { PERFORMANCE_METRICS_SPREADSHEET_RANK_ALIAS } from 'pages/Valuations/approaches/guidelinePublicCompanies/PerformanceMetrics/common/constants/performanceMetrics/sheetAliases';
import { evaluateNTMRevenueGrowth } from 'pages/ValuationsAllocation/common/createPerformanceMetrics/helpers';
import getApproachTableName from 'pages/ValuationsAllocation/util/getApproachTableName/getApproachTableName';
import { generateAllRowsExpression, getExpression, getObjectValue } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import {
  CellParserParams,
  ColumnVolatilityYears,
  CustomParserParams,
  EvaluateNTMRevenueGrowthReturn,
  GetCellParamsValueProps,
  GetCellParamsValueReturn,
  GetRankExpressionForPublicCompsParams,
} from './types';
import { ValuationsApproach } from '../../../../../../../common/types/valuation';
import {MARKET_CURRENCY, MEAN_LABEL, SPECIFIED_LABEL} from '../../../../../util/constants';
import { PERCENTILE_75 } from '../../../constants';
import MultipleTypeSelect from '../../../gpc/MultipleTypeSelect';
import MultipleTypeValueViewer from '../../../gpc/MultipleTypeValueViewer';
import {
  get25thPercentileExpression,
  get75thPercentileExpression,
  getMeanExpression,
  getMedianExpression,
} from '../utils/utils';

const {
  PERFORMANCE_METRICS_SPREADSHEET_COMPANY,
  PERFORMANCE_METRICS_SPREADSHEET_HEADER_SUBTITLE,
  PERFORMANCE_METRICS_SPREADSHEET_HEADER_TITLE,
} = SHEET_ALIASES_CONSTANTS;
const {
  PERFORMANCE_METRICS_SPREADSHEET_COLUMNS,
  PERFORMANCE_METRICS_SPREADSHEET_HEADERS,
  PERFORMANCE_METRICS_SPREADSHEET_VOLATILITY_COLUMNS,
} = SHEET_TITLES_CONSTANTS;

const EXCLUDE_TITLE_AND_SUBTITLE = 2;

const currencyRankedPerformanceMetrics = [
  PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.LTM_REVENUE,
  PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NTM_REVENUE,
  PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.LTM_EBITDA,
  PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NTM_EBITDA,
  PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.TANGIBLE_BOOK,
  PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NET_INCOME,
];

function getRankExpression({
  colNumber,
  allComparisonsExpression,
  approach,
  marketMatchesFinancialsCurrency,
}: GetRankExpressionForPublicCompsParams) {
  let expr = null;
  let hidden = true;
  if (allComparisonsExpression.length > 0 && colNumber < PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.VOLATILITY_1_YEAR) {
    // The array should not include the company being valued...
    const lastComparison = allComparisonsExpression.at(-1);
    const lastRowNumber = lastComparison ? Number(lastComparison.slice(1)) : 0;
    const companyRowKey = `@${lastRowNumber + 1}`;
    // ... but the rank should be calculated based on it
    if ((currencyRankedPerformanceMetrics as number[]).includes(colNumber) && !marketMatchesFinancialsCurrency) {
      const exchangeRateTableName = getApproachTableName({
        approach: approach as ValuationsApproach,
        tableSuffix: 'exchangeRates',
      })
      expr = `=PERCENTRANK(FILTER_ZEROS_AND_CONVERT_CURRENCY([${allComparisonsExpression}], ${exchangeRateTableName}.marketCurrency), ${companyRowKey} * ${exchangeRateTableName}.financialsCurrency)`;
    } else {
      expr = `=PERCENTRANK(FILTER_ZEROS_AS_OBJECT([${allComparisonsExpression}]), ${companyRowKey})`;
    }
    hidden = false;
  }
  return { expr, hidden };
}
type KeyNumber = 11 | 12 | 13;

const getCellParamsValue = ({
  valuationsApproachGpc,
  params,
  colNumber,
}: GetCellParamsValueProps): GetCellParamsValueReturn => {
  let value = '';

  const keyLabel = VOLATILITY_YEARS[colNumber as KeyNumber];
  const keyValue = VOLATILITY_YEARS_COMPANY_CELL[colNumber as KeyNumber];

  const customKey = VOLATILITY_YEARS_COMPANY_CELL[colNumber as KeyNumber];
  const label = valuationsApproachGpc?.[keyLabel as keyof ValuationApproachGPC] as string;
  value = valuationsApproachGpc?.[keyValue as keyof ValuationApproachGPC] as string;

  // allComparisonsExpression contains the rows that reference the public comps and the company being valued.
  // The latter is removed from the array to calculate the selected metric based on the public comps only.
  const { allComparisonsExpression: comparisonsExpr} = params;
  if (label !== SPECIFIED_LABEL) {
    switch (label) {
      case MEAN_LABEL:
        value = getMeanExpression(`[${comparisonsExpr.toString()}]`);
        break;
      case MEDIAN_LABEL:
        value = getMedianExpression(`[${comparisonsExpr.toString()}]`);
        break;
      case PERCENTILE_25:
        value = get25thPercentileExpression(`[${comparisonsExpr.toString()}]`);
        break;
      case PERCENTILE_75:
        value = get75thPercentileExpression(`[${comparisonsExpr.toString()}]`);
        break;
      default:
        value = get75thPercentileExpression(`[${comparisonsExpr.toString()}]`);
    }
  }

  return {
    customKey,
    value,
    label,
    expr: value ?? null,
  };
};

const cellParser = (params: CellParserParams) => {
  const {
    alphabet,
    colIndex,
    column,
    row,
    rowIndex,
    marketMatchesFinancialsCurrency,
    allComparisonsExpression,
    isDisabled,
    customData: cellOptions,
    initialObject: approach,
  } = params;
  const { initialObject } = getObjectValue(params);
  const { valuations_approach_gpc: valuationsApproachGpc } = getObjectValue(initialObject);
  const {
    one_year_volatility_percentile_selection,
    two_year_volatility_percentile_selection,
    five_year_volatility_percentile_selection,
  } = getObjectValue(valuationsApproachGpc);
  const tooltipMessages = [];

  const rowNumber = rowIndex + 1;
  const colNumber = colIndex + 1;
  const columnLegend = alphabet[colIndex];
  const key = columnLegend + rowNumber;

  const volatilitySelectionMap = {
    11: one_year_volatility_percentile_selection,
    12: two_year_volatility_percentile_selection,
    13: five_year_volatility_percentile_selection,
  };

  const { options } = getObjectValue(row);
  let {
    alias,
    className = '',
    colSpan = 1,
    columnId,
    component,
    enabled,
    expr = '',
    forceComponent,
    format = null,
    gridType = 'string',
    rowSpan,
    hidden,
    readOnly,
    dataEditor,
    valueViewer,
    isVolatility,
    dropdown,
    useScalarSpreadsheetCell,
  } = getObjectValue(row);

  let currencySpecification;
  let value = column[alias]?.value ?? '';
  let customKey;
  let NTMRevenueGrowthEvaluation: EvaluateNTMRevenueGrowthReturn = {};

  const VOLATILITY_COLUMNS_ARRAY = Object.values(PERFORMANCE_METRICS_SPREADSHEET_VOLATILITY_COLUMNS);

  // Aliases are in the format of performance_metrics_iqt572377879_0
  const comparison = valuationsApproachGpc?.gpc_comparison?.find(item => 
    alias && item.cap_iq_id && alias.includes(item.cap_iq_id.toLowerCase())
  );

  // Parse cell based on Row alias
  switch (alias) {
    // Header titles
    case PERFORMANCE_METRICS_SPREADSHEET_HEADER_TITLE:
      value = PERFORMANCE_METRICS_SPREADSHEET_HEADERS[colNumber].value;

      switch (colNumber) {
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.VOLATILITY_1_YEAR:
          className = `${className} ${PERFORMANCE_METRICS_SPREADSHEET_RANK_VOLATILITY_HEADER_CLASSNAME}`;
          rowSpan = 1;
          colSpan = 3;
          break;

        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.VOLATILITY_2_YEARS:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.VOLATILITY_5_YEARS:
          rowSpan = 1;
          break;

        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.TANGIBLE_BOOK:
          tooltipMessages.push(TANGIBLE_BOOK_VALUE_DESCRIPTION);
          break;
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NET_INCOME:
          tooltipMessages.push(NET_INCOME_DESCRIPTION);
          break;

        default:
          break;
      }
      break;

    // Header subtitles
    case PERFORMANCE_METRICS_SPREADSHEET_HEADER_SUBTITLE:
      value = PERFORMANCE_METRICS_SPREADSHEET_HEADERS[colNumber].subtitle;
      break;

    case PERFORMANCE_METRICS_SPREADSHEET_RANK_ALIAS:
      {
        const rankValues = getRankExpression({ colNumber, allComparisonsExpression, approach, marketMatchesFinancialsCurrency });
        expr = rankValues.expr;
        hidden = rankValues.hidden;
        gridType = 'percentage';
      }

      // Display (for the moment) values on Company Volatility columns
      if (VOLATILITY_COLUMNS_ARRAY.includes(colNumber as ColumnVolatilityYears)) {
        alias = VOLATILITY_YEARS[colNumber as ColumnVolatilityYears];
        hidden = false;
        gridType = 'string';
        format = oneDecimalPercentFormat;
        readOnly = isDisabled;
        isVolatility = true;
        if (colNumber === 11 || colNumber === 12 || colNumber === 13) {
          value = volatilitySelectionMap[colNumber] ?? PERCENTILE_75;
        }
        dropdown = true;
        useScalarSpreadsheetCell = true;
        dataEditor = (props: any) => <MultipleTypeSelect {...props} performanceCellOptions={cellOptions} />;
        valueViewer = (props: any) => <MultipleTypeValueViewer {...props} performanceCellOptions={cellOptions} />;
      }

      break;
    // Rest of the rows (excluding Header titles and subtitles)
    default:
      if (Number(value) === 0 && alias && ![
        PERFORMANCE_METRICS_SPREADSHEET_RANK_ALIAS,
        PERFORMANCE_METRICS_SPREADSHEET_COMPANY,
      ].includes(alias) && ![
        PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.VOLATILITY_1_YEAR,
        PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.VOLATILITY_2_YEARS,
        PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.VOLATILITY_5_YEARS,
      ].includes(colNumber as ColumnVolatilityYears)) {
        className += DISABLED_PERFORMANCE_ROW_CLASS;
      }
      // Parse cell based on Column number
      switch (colNumber) {
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.LTM_REVENUE:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NTM_REVENUE:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.LTM_EBITDA:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NTM_EBITDA:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.TANGIBLE_BOOK:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NET_INCOME:
          format = largeCurrencyFormat;
          gridType = 'number';
          if (alias === PERFORMANCE_METRICS_SPREADSHEET_COMPANY) {
            expr = '';
          } else {
            currencySpecification = MARKET_CURRENCY;
          }
          break;

        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.LTM_REVENUE_GROWTH:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.GROSS_MARGIN:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.EBITDA_MARGIN:
          format = oneDecimalPercentFormat;
          gridType = 'percentage';

          if (alias === PERFORMANCE_METRICS_SPREADSHEET_COMPANY) {
            expr = '';
          }
          break;

        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NTM_REVENUE_GROWTH:
          format = oneDecimalPercentFormat;
          gridType = 'percentage';

          NTMRevenueGrowthEvaluation = evaluateNTMRevenueGrowth({
            alphabet,
            alias,
            expr,
            rowNumber,
            value: value as string | number | null,
          });
          expr = NTMRevenueGrowthEvaluation?.expr ?? expr;
          value = NTMRevenueGrowthEvaluation?.value ?? value;

          break;

        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.VOLATILITY_1_YEAR:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.VOLATILITY_2_YEARS:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.VOLATILITY_5_YEARS:
          format = oneDecimalPercentFormatValidateFloatTrue;
          // Display the values from new comparisons when they are not saved yet
          if (!comparison?.id) {
            value = comparison?.[VOLATILITY_YEARS_MAPPING[colNumber] as keyof GPC] ?? '';
          }
          // The value is in percentage, we only need to apply the format
          value = Number(value ?? 0) / 100;
          gridType = GRID_NUMBER_CHECKBOX as GridType;
          component = <NumberCheckbox cell={{}} />;
          forceComponent = true;
          columnId = COMPARISON_VOLATILITY_YEARS_CELL[colNumber as KeyNumber];
          // Extended the GPC interface to include the enabled properties and leverage
          // a dynamic field name to avoid breaking the current case into 3 separate ones
          enabled = (comparison as GPC & Record<string, boolean>)?.[`${columnId}_enabled`] ?? true;

          if (alias === PERFORMANCE_METRICS_SPREADSHEET_COMPANY) {
            gridType = 'percentage';
            alias = VOLATILITY_YEARS_COMPANY_CELL[colNumber as KeyNumber];
            const cellProps = getCellParamsValue({ valuationsApproachGpc, params, colNumber, customKey });
            customKey = cellProps.customKey;
            value = getExpression({ expr: cellProps.value as string, columnLegend });
            expr = cellProps.expr;
          }
          break;

        default:
          break;
      }
      break;
  }

  return {
    [key]: {
      ...row,
      alias,
      className,
      colSpan,
      component,
      columnId,
      columnLegend,
      currencySpecification,
      enabled,
      expr: getExpression({ expr, columnLegend }),
      forceComponent,
      format,
      gridType,
      key,
      customKey,
      hidden,
      rowSpan,
      value,
      readOnly,
      dataEditor,
      valueViewer,
      isVolatility,
      dropdown,
      useScalarSpreadsheetCell,
      options,
      tooltipMessages: tooltipMessages.length ? tooltipMessages : undefined,
    } as ICell,
  };
};

const customParser = (params: CustomParserParams) => {
  const { columns, rowConfig, tableData } = params;
  const { isDisabled, customData, marketMatchesFinancialsCurrency } = tableData;

  let cells = {} as Cells;
  const alphabet = alphabetGenerator([], columns.length);

  const allComparisonsExpression = generateAllRowsExpression({
    rowConfig: rowConfig.filter(row => !row.forceComponent),
    excludedRows: EXCLUDE_TITLE_AND_SUBTITLE,
    excludeLastRow: true,
  });

  rowConfig.forEach((row, rowIndex: number) => {
    columns.forEach((column, colIndex: number) => {
      cells = {
        ...cells,
        ...cellParser({
          allComparisonsExpression,
          marketMatchesFinancialsCurrency,
          alphabet,
          colIndex,
          column,
          row,
          rowIndex,
          isDisabled,
          initialObject: params.initialObject,
          customData,
        }),
      };
    });
  });

  return cells;
};

export default customParser;
